import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { 
  Users, 
  Search, 
  Plus, 
  Edit, 
  Building2, 
  Shield,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  Clock,
  AlertCircle,
  Download,
  XCircle,
  Key,
  Mail
} from 'lucide-react';
import { systemAdminAPI } from '../../../services/api/systemAdminAPI';
import { useEscKeyHandler } from '../../../hooks/useEscKeyHandler';

const GlobalUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterTenant, setFilterTenant] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });

  const roles = [
    { value: 'SYSTEM_ADMIN', label: 'System Admin' },
    { value: 'tenant_admin', label: 'Tenant Admin' },
    { value: 'tenant_doctor', label: 'Doctor' },
    { value: 'tenant_nurse', label: 'Nurse' },
    { value: 'tenant_technician', label: 'Technician' },
    { value: 'tenant_patient', label: 'Patient' }
  ];

  const statuses = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [pagination.page, pagination.page_size, searchTerm, filterRole, filterTenant, filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Add Esc key handler for modal
  useEscKeyHandler(showModal, () => setShowModal(false));

  const loadTenants = async () => {
    try {
      const tenantsResponse = await systemAdminAPI.getAllTenants();
      const tenantData = tenantsResponse.data?.tenants || tenantsResponse.data || [];
      setTenants(Array.isArray(tenantData) ? tenantData : []);
    } catch (error) {
      console.error('Failed to load tenants:', error);
      toast.error('Failed to load tenants');
      setTenants([]);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      
      const response = await systemAdminAPI.getAllUsers(
        pagination.page,
        pagination.page_size,
        searchTerm,
        filterRole,
        filterTenant,
        filterStatus
      );
      
      const data = response.data;
      console.log('API Response:', response); // Debug log
      console.log('Response data:', data); // Debug log
      
      // Handle nested data structure from backend
      if (data && data.data) {
        setUsers(data.data.users || []);
        setPagination(data.data.pagination || {});
      } else {
        // Fallback for direct data structure
        setUsers(data.users || []);
        setPagination(data.pagination || {});
      }
      
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    setSearchLoading(false);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, page_size: newPageSize, page: 1 }));
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'role':
        setFilterRole(value);
        break;
      case 'tenant':
        setFilterTenant(value);
        break;
      case 'status':
        setFilterStatus(value);
        break;
      default:
        break;
    }
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const getTenantName = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? tenant.name : 'Unknown Tenant';
  };

  const getRoleLabel = (role) => {
    const roleObj = roles.find(r => r.value === role);
    return roleObj ? roleObj.label : role;
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'SYSTEM_ADMIN':
        return 'bg-[#DCEAF5] text-[#207CE5]';
      case 'tenant_admin':
        return 'bg-[#DCEAF5] text-[#207CE5]';
      case 'tenant_doctor':
        return 'bg-[#DCEAF5] text-[#008080]';
      case 'tenant_nurse':
        return 'bg-[#DCEAF5] text-[#00BCD4]';
      case 'tenant_technician':
        return 'bg-[#DCEAF5] text-[#4682B4]';
      case 'tenant_patient':
        return 'bg-[#DCEAF5] text-[#3D3D3D]';
      default:
        return 'bg-[#DCEAF5] text-[#3D3D3D]';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'SYSTEM_ADMIN':
        return <Shield className="h-3 w-3" />;
      case 'tenant_admin':
        return <Building2 className="h-3 w-3" />;
      case 'tenant_doctor':
        return <UserCheck className="h-3 w-3" />;
      case 'tenant_nurse':
        return <Users className="h-3 w-3" />;
      case 'tenant_technician':
        return <Clock className="h-3 w-3" />;
      case 'tenant_patient':
        return <AlertCircle className="h-3 w-3" />;
      default:
        return <Users className="h-3 w-3" />;
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  const handleSendMfaEnrollment = async (user) => {
    try {
      console.log(`=== FRONTEND: Sending MFA enrollment for user: ${user.email} ===`);
      toast.loading('Sending MFA enrollment email...', { id: 'mfa-enrollment' });
      
      const response = await systemAdminAPI.sendMfaEnrollment(user.id);
      
      if (response.data.success) {
        toast.success(`MFA enrollment email sent to ${user.email}`, { id: 'mfa-enrollment' });
        console.log('MFA enrollment email sent successfully:', response.data);
      } else {
        throw new Error(response.data.message || 'Failed to send MFA enrollment email');
      }
    } catch (error) {
      console.error('Error sending MFA enrollment email:', error);
      toast.error(
        error.response?.data?.detail || 
        error.message || 
        'Failed to send MFA enrollment email', 
        { id: 'mfa-enrollment' }
      );
    }
  };

  const handleResendPasswordSetup = async (user) => {
    try {
      console.log(`=== FRONTEND: Resending password setup for user: ${user.email} ===`);
      toast.loading('Resending password setup email...', { id: 'password-resend' });
      
      const response = await systemAdminAPI.resendPasswordSetup(user.id);
      
      if (response.data.success) {
        toast.success(`Password setup email resent to ${user.email}`, { id: 'password-resend' });
        console.log('Password setup email resent successfully:', response.data);
      } else {
        throw new Error(response.data.message || 'Failed to resend password setup email');
      }
    } catch (error) {
      console.error('Error resending password setup email:', error);
      toast.error(
        error.response?.data?.detail || 
        error.message || 
        'Failed to resend password setup email', 
        { id: 'password-resend' }
      );
    }
  };



  const UserModal = () => {
    const [formData, setFormData] = useState({
      first_name: '',
      last_name: '',
      email: '',
      role: 'tenant_technician',
      tenant_id: '',
      phone: '',
      status: 'active'
    });

    // Initialize form data when editing
    useEffect(() => {
      if (editingUser) {
        // Split the full name into first and last name
        const nameParts = (editingUser.name || '').split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        setFormData({
          first_name: firstName,
          last_name: lastName,
          email: editingUser.email || '',
          role: editingUser.role || 'tenant_technician',
          tenant_id: editingUser.tenant_id || '',
          phone: editingUser.phone || '',
          status: editingUser.status || 'active'
        });
      } else {
        // Reset form for create mode
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          role: 'tenant_technician',
          tenant_id: '',
          phone: '',
          status: 'active'
        });
      }
    }, [editingUser]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      // Prevent duplicate submissions
      if (isSubmitting) {
        return;
      }
      
      setIsSubmitting(true);
      try {
        if (editingUser) {
          // Update existing user - send separate first_name and last_name
          const updateData = {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            role: formData.role,
            status: formData.status,
            tenant_id: formData.tenant_id,
            phone_number: formData.phone
          };
          await systemAdminAPI.updateUser(editingUser.id, updateData);
          toast.success('User updated successfully');
        } else {
          // Create new user - send separate first_name and last_name
          const createData = {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            role: formData.role,
            tenant_id: formData.tenant_id,
            phone_number: formData.phone
          };
          const result = await systemAdminAPI.createUser(createData);
          toast.success(result.data?.message || 'User created successfully with password setup and MFA enrollment emails sent');
        }
        handleCloseModal();
        loadUsers();
      } catch (error) {
        toast.error(editingUser ? 'Failed to update user' : 'Failed to create user');
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingUser ? 'Edit User' : 'Create New User'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                    required
                  />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  required
                >
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tenant *
                </label>
                <select
                  value={formData.tenant_id}
                  onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  required={formData.role !== 'SYSTEM_ADMIN'}
                  disabled={formData.role === 'SYSTEM_ADMIN'}
                >
                  <option value="">Select Tenant</option>
                  {tenants.map(tenant => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-lg ${
                  isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-[#207CE5] hover:bg-[#0B1C2C]'
                }`}
              >
                {isSubmitting ? 'Creating...' : (editingUser ? 'Update User' : 'Create User')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Pagination
  const indexOfLastUser = pagination.page * pagination.page_size;

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#207CE5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Global User Management</h1>
          <p className="text-gray-600">Manage all users across all tenants</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
          <button
            onClick={handleCreateUser}
            className="flex items-center space-x-2 px-4 py-2 bg-[#207CE5] text-white rounded-lg hover:bg-[#0B1C2C] transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Add New User</span>
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Search & Filter</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">
              Showing {users.length} of {pagination.total_count} users
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                          <select
                value={filterRole}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
              >
              <option value="">All Roles</option>
              {roles.map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                          <select
                value={filterTenant}
                onChange={(e) => handleFilterChange('tenant', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
              >
              <option value="">All Organizations</option>
              {tenants.map(tenant => (
                <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                          <select
                value={filterStatus}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
              >
              <option value="">All Statuses</option>
              {statuses.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Items per page</label>
                          <select
                value={pagination.page_size}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
              >
              <option value="5">5 per page</option>
              <option value="10">10 per page</option>
              <option value="25">25 per page</option>
              <option value="50">50 per page</option>
            </select>
          </div>
        </div>
        
        <div className="mt-4 flex justify-center">
          <button
            onClick={handleSearch}
            disabled={searchLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-[#207CE5] text-white rounded-lg hover:bg-[#0B1C2C] disabled:opacity-50"
          >
            <Search className="h-4 w-4" />
            <span>{searchLoading ? 'Searching...' : 'Search'}</span>
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tenant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#207CE5] to-[#00BCD4] flex items-center justify-center">
                          <span className="text-sm font-medium text-white">
                            {user.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                      {getRoleIcon(user.role)}
                      <span className="ml-1">{getRoleLabel(user.role)}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.role === 'SYSTEM_ADMIN' ? 'System Wide' : getTenantName(user.tenant_id)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.status === 'ACTIVE' || user.status === 'active' ? 'bg-green-100 text-green-800' :
                      user.status === 'INACTIVE' || user.status === 'inactive' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-gray-400 hover:text-[#207CE5]"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.page_size) + 1} to {Math.min(pagination.page * pagination.page_size, pagination.total_count)} of {pagination.total_count} users
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.has_prev}
                className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-3 py-1 text-sm">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.has_next}
                className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {users.length === 0 && (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
          <p className="text-gray-500">
            {searchTerm || filterRole || filterTenant || filterStatus
              ? 'Try adjusting your search criteria'
              : 'Get started by creating your first user'}
          </p>
        </div>
      )}

      {/* Modal */}
      {showModal && <UserModal />}
    </div>
  );
};

export default GlobalUserManagement;